import { Button, EmptyState, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEnv } from '../../../context/env-context';
import { useStorefrontUrl } from '../../../hooks/use-storefront-url';
import { platformOrderUrl } from '../../../lib/platform-links';
import { shortDateTimeFormatter } from '../../../utils/format-date';
import { formatMoney } from '../../../utils/format-money';
import type { Purchase } from '../types';

function formatPurchaseType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function usePurchaseLink() {
  const env = useEnv();
  const storefrontUrl = useStorefrontUrl();

  return (purchase: Purchase): string | null => {
    if (
      !env.platform ||
      !env.store_id ||
      (purchase.type !== 'quote' && purchase.type !== 'order')
    ) {
      return null;
    }

    const purchaseId = purchase.purchase_identifiers.sparklayer;
    const customerId = purchase.customer_identifiers.sparklayer;
    const token = encodeURIComponent(`spark-purchase-${purchaseId}.${customerId}`);

    if (purchase.type === 'quote') {
      return storefrontUrl ? `${storefrontUrl}?spark-redirect=${token}` : null;
    }

    return platformOrderUrl(env.platform, env.store_id, purchase.purchase_identifiers.platform);
  };
}

export type ActivityTableProps = {
  purchases: Purchase[];
  loading?: boolean;
};

export function ActivityTable({ purchases, loading }: ActivityTableProps) {
  const getPurchaseLink = usePurchaseLink();

  if (!loading && purchases.length === 0) {
    return (
      <EmptyState
        title={t('features:activity.components.activity_table.empty_state.title')}
        subtitle={t('features:activity.components.activity_table.empty_state.subtitle')}
      />
    );
  }

  return (
    <>
      <Table
        caption={t('features:activity.components.activity_table.caption')}
        headings={[
          t('features:activity.components.activity_table.headings.date'),
          t('features:activity.components.activity_table.headings.order_number'),
          t('features:activity.components.activity_table.headings.type'),
          t('features:activity.components.activity_table.headings.shipping_address'),
          t('features:activity.components.activity_table.headings.amount'),
          '',
        ]}
        loading={loading}
        skeleton={{ rows: 5, cols: 6 }}
      >
        {purchases.map((purchase, index) => {
          const link = getPurchaseLink(purchase);
          return (
            <Table.Row
              key={purchase.purchase_identifiers.sparklayer}
              id={purchase.purchase_identifiers.sparklayer}
              index={index}
            >
              <Table.Cell>
                <Text>
                  {shortDateTimeFormatter.format(new Date(purchase.dates.calculated_submitted_at))}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text>
                  {purchase.purchase_identifiers.visible ??
                    purchase.purchase_identifiers.platform ??
                    purchase.purchase_identifiers.internal}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text>{formatPurchaseType(purchase.type)}</Text>
              </Table.Cell>
              <Table.Cell>
                <span className="max-w-xs">
                  <Text truncate>
                    {purchase.calculated_shipping_address?.length
                      ? purchase.calculated_shipping_address
                      : t('features:activity.components.activity_table.no_shipping_address')}
                  </Text>
                </span>
              </Table.Cell>
              <Table.Cell>
                <Text bold>
                  {formatMoney(
                    purchase.calculated_total.number,
                    purchase.calculated_total.currency,
                  )}
                </Text>
                <Text variant="secondary">{purchase.calculated_total.currency.toUpperCase()}</Text>
              </Table.Cell>
              <Table.Cell sticky>
                {link && (
                  <Button
                    variant="secondary"
                    onClick={() => window.open(link, '_blank')}
                    icon="externalLink"
                  >
                    {t('buttons:view')}
                  </Button>
                )}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table>
      {purchases.length > 499 && (
        <Text variant="secondary">
          {t('features:activity.components.activity_table.overflow_warning')}
        </Text>
      )}
    </>
  );
}
